import React, { useEffect, useState, useRef } from "react";
import { Button, Spinner, OverlayTrigger, Tooltip, Table, Badge } from "react-bootstrap";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import EditAttendanceModal from "./EditAttendanceModal";
import moment from "moment";
import { API_URL } from "../..";
import TimesheetPayrollSlip from "./TimesheetPayrollSlip";
import TimesheetPayrollSlipMonthly from "./TimesheetPayrollSlipMonthly";

const TimesheetMonthlyView = ({ date, employee }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const tableRef = useRef();

  const startOfMonth = moment(date).startOf("month");
  const endOfMonth = moment(date).endOf("month");
  const today = moment().startOf("day");
  const monthDays = Array.from(
    { length: endOfMonth.date() },
    (_, i) => moment(startOfMonth).clone().add(i, "days")
  );

  // Fetch data
  const fetchMonthly = async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      query.append("month", startOfMonth.format("YYYY-MM"));
      if (employee) query.append("employeeId", employee);

      const res = await fetch(`${API_URL}/api/attendance/timesheet-log/monthly?${query}`);
      const json = await res.json();
      if (json.success) setData(json.data || []);
    } catch (e) {
      console.error("Monthly fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthly();
  }, [date, employee]);

  // Export PDF
  const handleExportPDF = async () => {
    const input = tableRef.current;
    const pdf = new jsPDF("l", "pt", "a4");
    const canvas = await html2canvas(input, { scale: 1.3 });
    const imgData = canvas.toDataURL("image/png");
    const imgWidth = 800;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.setFontSize(18);
    pdf.text(`Monthly Timesheet - ${moment(date).format("MMMM YYYY")}`, 40, 30);
    pdf.addImage(imgData, "PNG", 40, 50, imgWidth, imgHeight);
    pdf.save(`Timesheet_${moment(date).format("YYYY_MM")}.pdf`);
  };

  // Helpers
  const formatTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm A") : "--");
  const canEdit = (day) => !moment(day).isAfter(today);

  const getColorClass = (status , isFuture) => {

    switch (status) {
      case "schedule off":
        return "bg-secondary-subtle text-muted";
      case "absent":
        return "bg-danger text-white";
      case "late":
        return "bg-warning text-dark";
      case "early":
      case "on-time":
        return "bg-success text-white";
      default:
        return "bg-light text-dark";
    }
   
  };
  if (employee && data && data.user) {
    if(loading){
      return(<div className="text-center py-4">
      <Spinner animation="border" /> Loading...
    </div>);
    }
    return <TimesheetPayrollSlipMonthly  employee={employee} date={date} data={data} />
    
  }
  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Monthly Timesheet ({moment(date).format("MMMM YYYY")})</h5>
        <Button variant="outline-primary" onClick={handleExportPDF}>
          <i className="bi bi-file-earmark-pdf me-2"></i>Download PDF
        </Button>
      </div>

      <div className="card-body p-3" ref={tableRef}>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" /> Loading...
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: "80vh" }}>
            <Table bordered hover className="align-middle text-center">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 180 }}>Employee</th>
                  {monthDays.map((d) => (
                    <th key={d.format("YYYY-MM-DD")}>{d.format("DD")}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((emp) => (
                    <tr key={emp.user.id}>
                      <td className="fw-semibold text-start">{emp.user?.name}</td>
                      {monthDays.map((day) => {
                        const dayStr = day.format("YYYY-MM-DD");
                        const rec = emp.attendance?.find(
                          (r) => r.date === dayStr || r.attendance_date === dayStr
                        );
                        const overtime = rec?.attendanceOvertimes?.[0];
                        const isFuture = moment(dayStr).isAfter(today);

                        return (
                          <td
                            key={dayStr}
                            className={`${isFuture ? 'bg-secondary-subtle text-muted' :  getColorClass(rec?.status, dayStr)} small position-relative`}
                            style={{
                              cursor: canEdit(dayStr) ? "pointer" : "not-allowed",
                              minWidth: rec?.status === "absent" ? "45px" : "90px",
                              padding: rec?.status === "absent" ? "5px" : "7px",
                            }}
                            onClick={() => {
                              if (canEdit(dayStr))
                                setEditRecord(rec || { user_id: emp.user.id, date: dayStr });
                            }}
                          >
                            {isFuture ? (
                              <Badge bg="light" text="dark">Upcoming</Badge>
                            ) : rec ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>
                                    <div style={{ textAlign: "left" }}>
                                      <strong>Status:</strong> {rec.status == 'on-time' || rec.status ==='early' ? 'PRESENT' : String(rec.status).toUpperCase() || "N/A"} <br />
                                      <strong>Punch In:</strong> {formatTime(rec.punch_in_time)} <br />
                                      <strong>Punch Out:</strong> {formatTime(rec.punch_out_time)} <br />
                                      <strong>Work:</strong> {rec.total_work_hours || "0"} hrs <br />
                                      <strong>Break:</strong> {rec.total_break_hours || "0"} hrs
                                      {overtime && (
                                        <>
                                          <br />
                                          <strong>Overtime:</strong> {overtime.start_time}–{overtime.end_time}
                                        </>
                                      )}
                                    </div>
                                  </Tooltip>
                                }
                              >
                                <div>
                                  <div className="fw-bold small">
                                    {formatTime(rec.punch_in_time)} - {formatTime(rec.punch_out_time)}
                                  </div>
                                  <small className="text-uppercase">{rec.status == 'on-time' || rec.status === 'early' ? 'PRESENT' : String(rec.status).toUpperCase()}</small>
                                </div>
                              </OverlayTrigger>
                            ) : (
                              <div className="fw-bold text-white">A</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={monthDays.length + 1} className="text-center py-4">
                      No attendance found for this month.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {editRecord && (
        <EditAttendanceModal
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onUpdated={fetchMonthly}
        />
      )}
    </div>
  );
};

export default TimesheetMonthlyView;
